import { Search, WifiOff } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { AccountMenu } from './AccountMenu'
import { AthleteSwitcher } from './AthleteSwitcher'
import { DateRangeControl } from './DateRangeControl'
import { SyncStatusChip } from './SyncStatusChip'
import { ThemeToggle } from './ThemeToggle'
import { TrimetricMark } from '@/components/brand/Logo'
import { Kbd } from '@/components/ui/Kbd'
import { useOnline } from '@/lib/online'
import { cn } from '@/lib/cn'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import { useHideOnScroll } from '@/hooks/useHideOnScroll'

export interface TopBarProps {
  /** Opens the command palette (⌘K). */
  onOpenSearch: () => void
}

const OFFLINE_LABEL = 'Offline, showing saved data'

/**
 * The pages that read the global range (Fitness, Wellness and its metrics, the Activities list).
 * Everywhere else it would do nothing, so it is hidden rather than left as a dead control.
 */
const RANGE_PATH = /^\/a\/[^/]+\/(fitness|wellness(\/[^/]+)?|activities)\/?$/

export function showsDateRange(pathname: string, search = ''): boolean {
  // The Routes tab of Activities covers every repeat, not a date range.
  return RANGE_PATH.test(pathname) && !/[?&]view=routes(&|$)/.test(search)
}

/**
 * Top bar: athlete switcher, global date range, search, sync status, theme toggle, account.
 * On a phone it is one slim row (mark, athlete, range, search, account) that slides away while
 * scrolling down and returns on the way back up; theme and sync live in the tab bar's More sheet.
 */
export function TopBar({ onOpenSearch }: TopBarProps) {
  const online = useOnline()
  const desktop = useIsDesktop()
  const hidden = useHideOnScroll({ enabled: !desktop })
  const location = useLocation()
  const showRange = showsDateRange(location.pathname, location.search)

  return (
    <header
      data-print-hide
      className={cn(
        'sticky top-0 z-30 border-b border-[var(--panel-border)] bg-[color-mix(in_oklab,var(--bg)_72%,transparent)] backdrop-blur-xl backdrop-saturate-150',
        'transition-transform duration-300 ease-[var(--ease-out)]',
        hidden && '-translate-y-full',
      )}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {desktop ? (
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <AthleteSwitcher />
            {showRange && <DateRangeControl />}
          </div>
          <div className="flex items-center gap-3">
            {!online && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface-2 px-2.5 py-1 text-caption text-ink-2">
                <WifiOff className="size-3.5 shrink-0" aria-hidden="true" />
                {OFFLINE_LABEL}
              </span>
            )}
            <button
              type="button"
              onClick={onOpenSearch}
              className="inline-flex items-center gap-2 rounded-md border border-line-strong bg-surface px-2.5 py-1.5 text-small text-ink-2 hover:bg-surface-2 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              <Search className="size-3.5" aria-hidden="true" />
              <span>Search</span>
              <Kbd>⌘K</Kbd>
            </button>
            <SyncStatusChip />
            <ThemeToggle />
            <AccountMenu />
          </div>
        </div>
      ) : (
        <div className="flex h-14 items-center gap-2 px-4">
          <TrimetricMark size={26} title="Trimetric" className="shrink-0" />
          <div className="min-w-0 flex-1">
            <AthleteSwitcher compact />
          </div>
          {!online && (
            <span
              role="status"
              title={OFFLINE_LABEL}
              className="inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-line bg-surface-2 text-ink-2"
            >
              <WifiOff className="size-4" aria-hidden="true" />
              <span className="sr-only">{OFFLINE_LABEL}</span>
            </span>
          )}
          {showRange && <DateRangeControl compact />}
          <button
            type="button"
            onClick={onOpenSearch}
            aria-label="Search"
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-full text-ink-2 transition-transform duration-150 hover:bg-surface-2 hover:text-ink active:scale-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <Search className="size-[18px]" aria-hidden="true" />
          </button>
          <AccountMenu />
        </div>
      )}
    </header>
  )
}
